import { forwardRef, useMemo } from 'react';
import { Noop } from 'react-hook-form';

import {
  getLogicBuilderOperators,
  QueryBuilderFieldGroup,
} from '@dotfile/shared/domain';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  HStack,
  Spinner,
  VStack,
} from '../../..';
import { createRuleGroupValidator } from './helpers';
import { QueryBuilder, QueryBuilderHandle } from './query-builder';
import { useQueryBuilderLogic } from './use-query-builder-logic';

type JsonLogicBuilderProps = {
  fields: QueryBuilderFieldGroup[];
  isLoading: boolean;
  onChange: (...event: unknown[]) => void;
  onBlur: Noop;
  disabled?: boolean;
  name: string;
  id: string;
  required?: boolean;
};

export const JsonLogicBuilder = forwardRef<
  QueryBuilderHandle,
  JsonLogicBuilderProps
>(
  (
    {
      fields,
      isLoading,
      onChange,
      onBlur,
      required = true,
      ...props
    }: JsonLogicBuilderProps,
    ref,
  ) => {
    const { parsedLogic, hasExtraneousVariables, handleLogicChange } =
      useQueryBuilderLogic({
        fieldName: props.name,
        fields,
        isLoading,
      });

    const validator = useMemo(
      () => createRuleGroupValidator(required),
      [required],
    );

    if (isLoading) {
      return <Spinner size="sm" />;
    }

    return (
      <VStack spacing={4} align="stretch">
        {hasExtraneousVariables && (
          <Alert width="auto" status="warning" mb={4}>
            <HStack>
              <AlertIcon />
              <VStack spacing={0} align="flex-start">
                <AlertTitle>
                  Properties related to this logic were deleted
                </AlertTitle>
                <AlertDescription>
                  Update and save new logic to resolve this warning
                </AlertDescription>
              </VStack>
            </HStack>
          </Alert>
        )}
        <QueryBuilder
          {...props}
          ref={ref}
          defaultValue={parsedLogic}
          onChange={(query) => {
            handleLogicChange(query);
            onBlur();
          }}
          fields={fields}
          getOperators={getLogicBuilderOperators}
          validator={validator}
        />
      </VStack>
    );
  },
);

JsonLogicBuilder.displayName = 'JsonLogicBuilder';
